/**
 * Cold-load auth helper — invoked by App.tsx on first React mount when no
 * in-memory token is present. Encapsulates the server-backed refresh decision
 * so the side effects can be unit-tested in isolation.
 *
 * Decision tree (server-backed refresh + 7-day idle gate):
 *   1. !hasCookieKnown() → onNoHint()  // no cookie → fresh user or post-logout
 *   2. lastActive > 7 days → clearCookieKnown() + clearAccountHint() +
 *      clearLastActive() + onSilentRefreshFailed('idle_7d_exceeded')
 *   3. try postRefresh()
 *      - SessionExpired (no_session / invalid_session / grant_revoked):
 *        → clearCookieKnown() + clearAccountHint() + clearLastActive()
 *        + onSilentRefreshFailed({ reason: <mapped> })
 *      - SessionExpired('app_check_failed'):
 *        → onSilentRefreshFailed({ reason: 'browser_blocked' })
 *        → KEEP cookieKnown (cookie is still valid; only App Check failed)
 *      - UpstreamUnavailable:
 *        → onUpstreamError() (optional — toast; do NOT clear state)
 *      - success:
 *        → setLastActive(now) + setToken({...}) + onTokenRefreshed()
 *
 * Cross-references:
 *   - cookieKnown flag is set by handleAuthenticated (after exchange) and
 *     cleared by handleLogout.
 *   - lastActive is written ONLY here on success (Decision: single write
 *     point for lastActive — the 401 retry inside fetchWithAuth must NOT
 *     extend the idle window).
 */

import { SessionExpired, UpstreamUnavailable } from '@/lib/auth/serverRefresh';
import { SESSION_MAX_IDLE_MS } from '@/lib/auth/lastActive';
import type { TokenResp } from './coldLoad.types';
import type { SilentRefreshFailureReason } from '@/lib/auth/silentRefreshErrors';

export interface ColdLoadDeps {
  /** Returns the persisted email hint (`mochia.account.v1`) or null. Used for display only — NOT for routing. */
  getAccountHint: () => string | null;
  /** Removes the persisted email hint. Called when refresh fails to prevent a stale hint looping. */
  clearAccountHint: () => void;
  /** Returns true when a server-backed mochia_rt cookie is expected to exist. */
  hasCookieKnown: () => boolean;
  /** Removes the cookieKnown flag. Called on non-upstream session failures and idle gate trips. */
  clearCookieKnown: () => void;
  /** Server-backed refresh — calls /api/auth/refresh. Returns { access_token, expires_at }. */
  postRefresh: () => Promise<TokenResp>;
  /** Stamps the freshly acquired token into the in-memory session. */
  setToken: (input: { token: string; email: string | null; expiresAt: number | null }) => void;
  /** Fired after setToken succeeds. App uses this to kick off the bootstrap pipeline. */
  onTokenRefreshed: () => void;
  /** Fired when no cookieKnown flag is present. App will render LoginScreen. */
  onNoHint: () => void;
  /**
   * Fired when refresh fails with a classified reason.
   * App forwards this to LoginScreen for actionable copy.
   */
  onSilentRefreshFailed: (info: { reason: SilentRefreshFailureReason }) => void;
  /**
   * Optional. Fired when /api/auth/refresh returns UpstreamUnavailable.
   * The session state is NOT cleared — this is a transient upstream failure.
   * App should surface a toast but remain on the current screen.
   */
  onUpstreamError?: () => void;
  /** Returns the persisted lastActive timestamp (ms) or null. */
  getLastActive: () => number | null;
  /** Persist a lastActive timestamp (ms). */
  setLastActive: (ts: number) => void;
  /** Remove the persisted lastActive value entirely. */
  clearLastActive: () => void;
  /** Source of "now" — injected for deterministic tests. Defaults to Date.now. */
  now?: () => number;
  /** Override for SESSION_MAX_IDLE_MS — test-only. Defaults to 7 days. */
  sessionMaxIdleMs?: number;
}

/**
 * Map a SessionExpired reason to a SilentRefreshFailureReason for LoginScreen copy.
 * app_check_failed maps to 'browser_blocked' because the most likely cause in
 * a PWA standalone environment is reCAPTCHA being blocked — the user-facing
 * copy about "browser blocked" is the most actionable message.
 */
function mapSessionExpiredReason(
  reason: SessionExpired['reason'],
): SilentRefreshFailureReason {
  switch (reason) {
    case 'no_session':
      return 'no_session';
    case 'invalid_session':
      return 'invalid_session';
    case 'grant_revoked':
      return 'grant_revoked';
    case 'app_check_failed':
      return 'browser_blocked';
    default:
      return 'unknown';
  }
}

export async function runColdLoadAuth(deps: ColdLoadDeps): Promise<void> {
  // Step 1: check cookieKnown flag — no flag means no server cookie to refresh.
  if (!deps.hasCookieKnown()) {
    deps.onNoHint();
    return;
  }

  const now = (deps.now ?? Date.now)();
  const idleLimit = deps.sessionMaxIdleMs ?? SESSION_MAX_IDLE_MS;
  const lastActive = deps.getLastActive();

  // Step 2: 7-day absolute idle gate.
  // null / future lastActive is treated as "freshly active" (legacy migration / clock rewind).
  if (lastActive !== null) {
    const idle = now - lastActive;
    if (idle > idleLimit) {
      deps.clearCookieKnown();
      deps.clearAccountHint();
      deps.clearLastActive();
      deps.onSilentRefreshFailed({ reason: 'idle_7d_exceeded' });
      return;
    }
  }

  // Step 3: attempt server-backed refresh.
  let resp: TokenResp;
  try {
    resp = await deps.postRefresh();
  } catch (err) {
    if (err instanceof UpstreamUnavailable) {
      // Transient upstream failure — do NOT clear cookie or session state.
      // Surface a toast so the user knows something is happening.
      if (deps.onUpstreamError) deps.onUpstreamError();
      return;
    }
    if (err instanceof SessionExpired) {
      const mappedReason = mapSessionExpiredReason(err.reason);
      if (err.reason === 'app_check_failed') {
        // App Check failure — cookie is still valid; only App Check token failed.
        // KEEP cookieKnown so the next cold load can retry (with a fresh App Check token).
        deps.onSilentRefreshFailed({ reason: mappedReason });
      } else {
        // Cookie is gone/invalid/revoked — clear all state.
        deps.clearCookieKnown();
        deps.clearAccountHint();
        deps.clearLastActive();
        deps.onSilentRefreshFailed({ reason: mappedReason });
      }
      return;
    }
    // Unknown error — treat as session failure, clear state.
    deps.clearCookieKnown();
    deps.clearAccountHint();
    deps.clearLastActive();
    deps.onSilentRefreshFailed({ reason: 'unknown' });
    return;
  }

  // Success: write lastActive BEFORE onTokenRefreshed so the bootstrap
  // pipeline never sees a stale lastActive value mid-flight.
  deps.setLastActive(now);
  deps.setToken({
    token: resp.access_token,
    email: deps.getAccountHint(),
    expiresAt: resp.expires_at,
  });
  deps.onTokenRefreshed();
}
